import React, { useEffect } from 'react';
import {
  Drawer, Form, Input, Select, Tabs, Upload, message, Alert, Space, Button, Row, Col,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { TARGET_TYPES, resolveCouponStatus, DOMAIN_NODES } from '../couponData';

const { TextArea } = Input;

/**
 * 发放卡券 Drawer（发放记录页发起发放 / 卡券列表行内发放 共用）
 * lockCoupon = true：卡券已固定（行内入口）；false：需在 Drawer 内选择卡券
 * codes：券码池（用于按「卡券 + 适用节点」统计域节点总卡券 / 可发放数）
 */
const IssueDrawer = ({ open, coupon, coupons = [], codes = [], lockCoupon = false, onClose }) => {
  const [form] = Form.useForm();
  const selectedId = Form.useWatch('couponId', form);
  const selectedNode = Form.useWatch('domainNode', form);
  const currentCoupon = lockCoupon ? coupon : coupons.find((c) => c.id === selectedId);

  // 已选卡券 + 适用节点时，统计该域节点下的券码
  const nodeCodes = currentCoupon && selectedNode
    ? codes.filter((c) => c.couponId === currentCoupon.id && c.domainNode === selectedNode)
    : [];
  const nodeTotal = nodeCodes.length;
  const nodeIssuable = nodeCodes.filter((c) => c.status === 'unissued').length;

  useEffect(() => {
    if (open) {
      form.resetFields();
      if (lockCoupon && coupon) form.setFieldsValue({ couponId: coupon.id });
    }
  }, [open, coupon, lockCoupon, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const target = lockCoupon ? coupon : coupons.find((c) => c.id === values.couponId);
      if (!target) return;
      const lines = String(values.targets || '')
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
      const available = codes.filter(
        (c) => c.couponId === target.id && c.domainNode === values.domainNode && c.status === 'unissued',
      ).length;
      if (lines.length > available) {
        message.error(`可发放券码不足：该域节点下可发放 ${available} 张，本次需发放 ${lines.length} 张，请先制券`);
        return;
      }
      message.success(`已提交发放 ${lines.length} 张券码，将从券码池分配并绑定到目标`);
      onClose();
    } catch (e) {
      // 校验失败由表单提示
    }
  };

  return (
    <Drawer
      title="发起发放"
      width={640}
      open={open}
      onClose={onClose}
      forceRender
      footer={(
        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button onClick={onClose}>取消</Button>
            <Button type="primary" onClick={handleSubmit}>提交发放</Button>
          </Space>
        </div>
      )}
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="发放为可选操作，用于将券码定向预绑给指定目标；未发放券码同样可被用户凭码核销。"
      />
      <Form form={form} layout="vertical">
        <Row gutter={[16, 20]}>
          <Col span={24}>
            {lockCoupon ? (
              <Form.Item label="卡券名称">
                <Input value={coupon?.name} disabled />
              </Form.Item>
            ) : (
              <Form.Item
                name="couponId"
                label="选择卡券"
                extra="仅展示「可用且存在可发放券码」的卡券"
                rules={[{ required: true, message: '请选择卡券' }]}
              >
                <Select placeholder="请选择要发放的卡券" showSearch optionFilterProp="children">
                  {coupons
                    .filter((c) => resolveCouponStatus(c) === 'available' && c.made - c.issued > 0)
                    .map((c) => (
                      <Select.Option key={c.id} value={c.id}>
                        {`${c.name}（${c.id}）· 可发放 ${c.made - c.issued} 张`}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            )}
          </Col>
          <Col span={24}>
            <Form.Item
              name="domainNode"
              label="适用节点"
              extra="选择本次发放券码所属的服务器域节点（必选）"
              rules={[{ required: true, message: '请选择适用节点' }]}
            >
              <Select placeholder="请选择适用节点">
                {Object.entries(DOMAIN_NODES).map(([value, cfg]) => (
                  <Select.Option key={value} value={value}>{cfg.label}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          {currentCoupon && selectedNode && (
            <Col span={24}>
              <Alert
                type="info"
                showIcon
                message={`域节点总卡券：${nodeTotal}，可发放：${nodeIssuable}`}
              />
            </Col>
          )}
          <Col span={24}>
            <Form.Item
              name="targetType"
              label="发放目标类型"
              rules={[{ required: true, message: '请选择发放目标类型' }]}
            >
              <Select placeholder="请选择发放目标的标识类型">
                {TARGET_TYPES.map((t) => (
                  <Select.Option key={t} value={t}>{t}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Tabs
          items={[
            {
              key: 'manual',
              label: '手动录入',
              children: (
                <Form.Item
                  name="targets"
                  label="发放标识"
                  extra="每行一条，支持粘贴多行；系统会将发放标识映射到用户 UID，券最终在用户上生效"
                  rules={[{ required: true, message: '请输入发放标识' }]}
                >
                  <TextArea rows={8} placeholder="每行一个，如：&#10;QEBUHL26012700IA&#10;QEBUHL26012811AB" />
                </Form.Item>
              ),
            },
            {
              key: 'import',
              label: '批量导入',
              children: (
                <Upload.Dragger
                  accept=".xlsx,.xls,.csv"
                  beforeUpload={() => {
                    message.success('文件已解析，发放任务提交后从券码池分配券码（原型演示）');
                    return false;
                  }}
                  maxCount={1}
                >
                  <p className="ant-upload-drag-icon"><UploadOutlined /></p>
                  <p className="ant-upload-text">点击或拖拽文件到此处上传</p>
                  <p className="ant-upload-hint">支持 xlsx / csv，单次最多 10000 条</p>
                </Upload.Dragger>
              ),
            },
          ]}
        />
      </Form>
    </Drawer>
  );
};

export default IssueDrawer;
